import { Box } from '@mentimeter/ragnar-ui/box';
import { scaleBand, scaleLinear } from 'd3-scale';
import { useCallback, useMemo } from 'react';
import { AvatarStackSvg } from '../components/identified-responses/AvatarStackSvg';
import {
  getAvatarPadding,
  getAvatarRadius,
} from '../components/identified-responses/utils';
import type {
  ChoicesData,
  ChoicesStaticOptions,
  ChoicesVisualization,
} from '../types';
import { getPercentageGetter } from '../utils/getPercentageGetter';
import { getElement } from '../utils/non-empty-array';
import { SvgWithFont } from '../utils/SvgWithFont';
import type {
  RenderChoiceLabel,
  RenderChoiceVisualization,
  RenderChoiceVisualizationItem,
} from '../utils/types/render-options-by-slide-type/types';
import { Bar } from './Bar';
import { BarsAxisLabels } from './BarsAxisLabels';
import {
  AVATAR_LOW_OPACITY,
  LABELS_HEIGHT,
  LOW_OPACITY,
  MAX_BAR_WIDTH,
  MAX_BAR_WITH_IMAGE_WIDTH,
  MIN_BAR_HEIGHT,
  MIN_MARGIN_TOP,
  VALUE_TEXT_HEIGHT,
  VALUE_TEXT_MARGIN,
} from './constants';
import { getScoreYAdjustment } from './getScoreYAdjustment';
import { HiddenScoreBars } from './hidden-score-bars/HiddenScoreBars';
import { Score, type CorrectnessIndicatorStatus } from './Score';
import { useBarHover } from './useBarHover';
import { calculateBarSpacing } from './utils/calculateBarSpacing';

interface BarsProps extends ChoicesVisualization {
  spacingPercentage?: number;
  onGetImages:
    | (({ width }: { width: number }) => Record<string, string>)
    | undefined;
  isScoreHidden: boolean;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  renderChoiceVisualization?: RenderChoiceVisualization | undefined;
}

export const Bars = ({
  colors,
  textColor,
  data,
  staticOptions,
  onGetImages,
  keyAccessor = (d: ChoicesData | ChoicesStaticOptions) => String(d.id),
  labelAccessor = (d: ChoicesStaticOptions) => d.label,
  valueAccessor = (d: ChoicesData) => d.score,
  spacingPercentage,
  width: rootWidth,
  height: rootHeight,
  shouldShowScorePercentage = false,
  disableAnimations = false,
  shouldShowCorrect = false,
  identities,
  correctAnswerAccessor = (d) => d.isMarkedCorrect,
  onClickOption,
  isScoreHidden,
  fontFamily,
  renderChoiceLabel,
  renderChoiceVisualization,
}: BarsProps) => {
  const { isBarHovered, onBarHover } = useBarHover();

  const hasImages = onGetImages !== undefined;
  const maxBarWidth = hasImages ? MAX_BAR_WITH_IMAGE_WIDTH : MAX_BAR_WIDTH;

  const calculatedSpacing =
    spacingPercentage ??
    calculateBarSpacing({
      containerWidth: rootWidth,
      numBars: staticOptions.length,
      hasImages,
    });

  const maxBoundedWidth =
    (maxBarWidth * staticOptions.length) / (1 - calculatedSpacing);
  const boundedWidth = Math.min(rootWidth, maxBoundedWidth);
  const boundsLeftMargin = (rootWidth - boundedWidth) / 2;

  const discreteValues = staticOptions.map(keyAccessor);
  const discreteScale = scaleBand()
    .domain(discreteValues)
    .range([0, boundedWidth])
    .paddingInner(calculatedSpacing)
    .paddingOuter(0.00001); // having this at 0 makes the first bar disappear, I have no idea what I'm doing wrong

  const imageWidth = discreteScale.bandwidth();
  const images = onGetImages ? onGetImages({ width: imageWidth }) : null;
  const imageHeight = images ? imageWidth : 0;

  const totalMarginTop = MIN_MARGIN_TOP + imageHeight;
  const svgHeight = Math.max(rootHeight - LABELS_HEIGHT, 0);
  const boundedHeight = Math.max(svgHeight - totalMarginTop, 0);

  const maxValue = Math.max(...data.map(valueAccessor));
  const domainMax = maxValue > 0 ? maxValue : 1;
  const linearScale = scaleLinear()
    .domain([0, domainMax])
    .range([MIN_BAR_HEIGHT, boundedHeight]);

  const getScoreDisplayText = (score: number): string => {
    if (shouldShowScorePercentage) {
      const getPercentage = getPercentageGetter(
        shouldShowScorePercentage.percentOf,
      );
      return getPercentage(score).toFixed(0) + '%';
    } else {
      return score.toString();
    }
  };

  const getCorrectnessIndicatorStatus = (
    choicesData: ChoicesData,
  ): CorrectnessIndicatorStatus => {
    const staticOption = staticOptions.find(
      (option) => keyAccessor(option) === keyAccessor(choicesData),
    );
    const isCorrect = Boolean(
      staticOption && correctAnswerAccessor(staticOption),
    );
    return {
      isVisible: shouldShowCorrect,
      isCorrect,
    };
  };

  const getRectanglePosition = useCallback(
    (d: ChoicesData) => {
      const value = valueAccessor(d);
      const x = discreteScale(keyAccessor(d));
      const y = boundedHeight - linearScale(value);
      const width = discreteScale.bandwidth();
      const height = linearScale(value);
      const step = discreteScale.step();
      const gap = step - width;

      return { x, y, width, height, gap };
    },
    [discreteScale, boundedHeight, linearScale, keyAccessor, valueAccessor],
  );

  const visualization = (
    <SvgWithFont width={rootWidth} height={svgHeight} fontFamily={fontFamily}>
      <g
        data-testid="bounds"
        width={boundedWidth}
        height={boundedHeight}
        transform={`translate(${boundsLeftMargin}, ${totalMarginTop})`}
      >
        {isScoreHidden ? (
          <HiddenScoreBars
            data={data}
            staticOptions={staticOptions}
            xScale={discreteScale}
            boundedHeight={boundedHeight}
            colors={colors}
            disableAnimations={disableAnimations}
            images={images}
            keyAccessor={keyAccessor}
          />
        ) : (
          <g data-name="bars">
            {data.map((d, i) => {
              const value = valueAccessor(d);
              const color = getElement(colors, i);
              const { x, y, width } = getRectanglePosition(d);
              const backgroundImage = images?.[keyAccessor(d)];

              const correctnessIndicatorStatus =
                getCorrectnessIndicatorStatus(d);
              const isCorrectAnswer =
                correctnessIndicatorStatus.isVisible &&
                correctnessIndicatorStatus.isCorrect;

              const isFullOpacity = !shouldShowCorrect || isCorrectAnswer;
              const barOpacity = isFullOpacity ? 1 : LOW_OPACITY;
              const avatarOpacity = isFullOpacity ? 1 : AVATAR_LOW_OPACITY;

              if (!x) {
                return null;
              }

              const identityList = identities?.find(
                (identity) => identity.choiceId === d.id,
              );

              const avatarRadius = getAvatarRadius(width);

              const avatarPadding = getAvatarPadding(avatarRadius);

              return (
                <g
                  data-value={value}
                  key={d.id}
                  onMouseEnter={() => onBarHover(d.id)}
                  onMouseLeave={() => onBarHover(null)}
                  onClick={() => onClickOption?.(d.id)}
                  cursor={onClickOption ? 'pointer' : 'default'}
                >
                  <Bar
                    image={images ? { imageUrl: backgroundImage } : undefined}
                    color={color}
                    x={x}
                    y={y}
                    width={width}
                    height={linearScale(value)}
                    disableAnimations={disableAnimations}
                    opacity={barOpacity}
                  />
                  {value > 0 && identityList && (
                    <AvatarStackSvg
                      identities={identityList.participants ?? []}
                      x={x + avatarPadding}
                      y={boundedHeight - avatarPadding}
                      color={color}
                      radius={avatarRadius}
                      opacity={avatarOpacity}
                      maxNumberOfVisibleAvatars={width < 100 ? 1 : 3}
                      colorShade={isBarHovered(d.id) ? 'hovering' : 'regular'}
                      totalVotes={value}
                    />
                  )}
                </g>
              );
            })}
          </g>
        )}
      </g>
    </SvgWithFont>
  );

  const visualizationChoices = useMemo(() => {
    if (typeof renderChoiceVisualization !== 'function') {
      return undefined;
    }

    const choices: Array<RenderChoiceVisualizationItem> = data.map((d) => {
      const id = keyAccessor(d);
      const position = getRectanglePosition(d);
      return { id, position };
    });

    return choices;
  }, [renderChoiceVisualization, data, keyAccessor, getRectanglePosition]);

  const visualizationOverride = renderChoiceVisualization?.({
    node: visualization,
    boundedWidth,
    boundsLeftMargin,
    choices: visualizationChoices ?? [],
  });

  if (svgHeight <= 0 || rootWidth <= 0) {
    return null;
  }

  return (
    <Box
      width={rootWidth}
      height={rootHeight}
      position="relative"
      data-testid="bars-visualization"
    >
      {visualizationOverride ?? visualization}
      <Box
        data-testid="scores"
        width={boundedWidth}
        height={boundedHeight}
        top={totalMarginTop}
        left={boundsLeftMargin}
        position="absolute"
        aria-hidden
        // to enable SVG behind to be clickable
        style={{ pointerEvents: 'none' }}
      >
        {data.map((d, i) => {
          const value = valueAccessor(d);
          const { x, y: rectangleY, width } = getRectanglePosition(d);
          const correctnessIndicatorStatus = getCorrectnessIndicatorStatus(d);

          const hasImage = images?.[keyAccessor(d)] !== undefined;

          const yAdjustment = getScoreYAdjustment({
            currentScore: value,
            identityList: identities?.find(
              (identity) => identity.choiceId === d.id,
            )?.participants,
            hasImage,
            barWidth: width,
            barHeight: linearScale(value),
          });

          if (!x) {
            return null;
          }

          return (
            <Score
              key={x}
              x={x}
              y={rectangleY - imageHeight - VALUE_TEXT_MARGIN - yAdjustment}
              barWidth={width}
              textColor={textColor}
              height={VALUE_TEXT_HEIGHT}
              scoreDisplayText={isScoreHidden ? '' : getScoreDisplayText(value)}
              correctnessIndicatorStatus={
                isScoreHidden
                  ? { ...correctnessIndicatorStatus, isVisible: false }
                  : correctnessIndicatorStatus
              }
              disableAnimations={disableAnimations}
            />
          );
        })}
      </Box>
      <BarsAxisLabels
        staticOptions={staticOptions}
        labelAccessor={labelAccessor}
        keyAccessor={keyAccessor}
        discreteScale={discreteScale}
        totalWidth={boundedWidth}
        totalHeight={LABELS_HEIGHT}
        marginLeft={boundsLeftMargin}
        marginTop={20}
        color={textColor}
        fontFamily={fontFamily}
        renderChoiceLabel={renderChoiceLabel}
      />
    </Box>
  );
};
